/**
 * Простой и централизованный способ подписки на событие scroll у window
 * Подписчик один, а обработчиков много
 *
 * @see isScrolling
 * @see onScroll
 * @see onScrollStarted
 * @see onScrollStopped
 *
 * @author Aleksandr Bavin
 * @date 10.06.2022
 */

import throttle from "OK/utils/throttle";

interface IScrollListener {
    (e?: MouseEvent): void;
}

const onScrollListeners: IScrollListener[] = [];
const onScrollStartedListeners: IScrollListener[] = [];
const onScrollStoppedListeners: IScrollListener[] = [];
let scrolling = false;
let scrollingStoppedTimer: number | null = null;
const throttleDelay = 100;
let isListeningScroll = false;

let listenersKeys: WeakMap<Function, string>;

if (WeakMap) {
    listenersKeys = new WeakMap();
}

// инициализация обработчиков - один раз
function initScrollListener () {
    if (!isListeningScroll) {
        isListeningScroll = true;
        window.addEventListener('scroll', scrollListener);
    }
}

// оповещаем onScroll обработчики
const notifyOnScrollListeners = throttle(throttleDelay, function (e: MouseEvent) {
    for (let i = 0, ln = onScrollListeners.length; i < ln; i++) {
        onScrollListeners[i](e);
    }

    scrollingStoppedTimer = setTimeout(scrollingStopped.bind(null, e), throttleDelay);
});

// основной обработчик скролла - ничего лишнего не добавлять!
function scrollListener (event: Event) {
    const e = event as MouseEvent;
    clearTimeout(scrollingStoppedTimer!);

    if (!scrolling) {
        scrollingStarted(e);
    }

    notifyOnScrollListeners(e);
}

// начали скроллить
function scrollingStarted (e: MouseEvent) {
    scrolling = true;

    for (let i = 0, ln = onScrollStartedListeners.length; i < ln; i++) {
        onScrollStartedListeners[i](e);
    }
}

// закончили скроллить
function scrollingStopped (e: MouseEvent) {
    scrolling = false;

    for (let i = 0, ln = onScrollStoppedListeners.length; i < ln; i++) {
        onScrollStoppedListeners[i](e);
    }
}

// отписка обработчика
function destroyListener(listener: (...args: any[]) => void, array: any[]) {
    const index = array.indexOf(listener);

    if (index !== -1) {
        array.splice(index, 1);
    }
}

// добавляет обработчик в массив и возвращает функцию для отписки
function createDestructor(key: string, listener: (...args: any[]) => void, array: any[]): () => void {
    array.push(listener);

    if (listenersKeys) {
        listenersKeys.set(listener, key);
    }

    return destroyListener.bind(null, listener, array);
}

export function isScrolling() {
    initScrollListener();
    return scrolling;
}

// добавляет обработчик на скролл
export function onScroll(key: string, listener: IScrollListener) {
    initScrollListener();
    return createDestructor(key, listener, onScrollListeners);
}

// добавляет обработчик на начало скролла
export function onScrollStarted(key: string, listener: IScrollListener) {
    initScrollListener();
    return createDestructor(key, listener, onScrollStartedListeners);
}

// добавляет обработчик на окончание скролла
export function onScrollStopped(key: string, listener: IScrollListener) {
    initScrollListener();
    return createDestructor(key, listener, onScrollStoppedListeners);
}
